import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Trophy, LineChart, ArrowRight, CheckCircle } from 'lucide-react';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const features = [
    'AI-related SaaS and software acquisition focus',
    'Software and online-business focus',
    'Founder and operator conversations grounded in product reality',
    'Long-term ownership and build quality mindset',
  ];

  return (
    <section id="about" className="py-24 relative overflow-hidden bg-gradient-to-b from-gray-50 to-white p-0 m-0 border-0" style={{ marginTop: 0, paddingTop: 0 }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          style={{ y: y1 }}
          className="absolute right-0 top-0 w-96 h-96 bg-primary-50 rounded-full filter blur-3xl opacity-30"
        ></motion.div>
        <motion.div
          style={{ y: y2 }}
          className="absolute -left-20 top-1/3 w-80 h-80 bg-secondary-50 rounded-full filter blur-3xl opacity-30"
        ></motion.div>
        <motion.div
          style={{ y: y3 }}
          className="absolute right-1/4 bottom-0 w-64 h-64 bg-accent-50 rounded-full filter blur-3xl opacity-20"
        ></motion.div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 p-0 m-0 border-0" style={{ marginTop: 96, paddingTop: 0 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div ref={ref} variants={container} initial="hidden" animate={inView ? 'show' : 'hidden'} className="max-w-xl">
              <motion.div variants={item} className="inline-flex items-center py-1.5 px-4 rounded-full bg-gradient-to-r from-primary-100 to-primary-50 text-primary-600 text-sm font-medium mb-4 shadow-sm">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="mr-2"
                >
                  <Shield size={16} />
                </motion.span>
                About Us
              </motion.div>

              <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
                Focused on AI SaaS and Online Business Acquisitions
              </motion.h2>

              <motion.p variants={item} className="text-lg text-gray-600 leading-relaxed mb-8">
                ACSE Capital is focused on acquiring and building AI-related SaaS startups, software products, and online businesses. Our work centers on digital opportunities where product quality, operating clarity, and execution matter.
              </motion.p>

              <motion.div variants={item} className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    className="flex items-center"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                      <CheckCircle size={14} className="text-primary-600" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 1.2 }}
              >
                <motion.a
                  whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)' }}
                  whileTap={{ scale: 0.95 }}
                  href="#contact"
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 transition-all duration-300"
                >
                  <span>See What We Focus On</span>
                  <ArrowRight size={18} className="ml-2" />
                </motion.a>
              </motion.div>
            </motion.div>
          </div>

          <div className="relative">
            <div className="w-full flex flex-col gap-6 md:grid md:grid-cols-3 md:gap-4 pb-16 md:pb-0">
              <motion.div
                whileHover={{ y: -10, rotateY: 5, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center text-center border border-gray-100 transform transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-50 rounded-2xl flex items-center justify-center mb-6 transform transition-transform duration-300 group-hover:scale-110">
                  <motion.div
                    animate={{ rotate: [0, 10, 0, -10, 0] }}
                    transition={{ duration: 5, repeat: Infinity, repeatType: 'reverse' }}
                  >
                    <Shield size={28} className="text-primary-600" />
                  </motion.div>
                </div>
                <h3 className="text-xl font-bold mb-4">Digital-First Scope</h3>
                <p className="text-gray-600">
                  We focus on software, SaaS, AI-enabled products, and online businesses with clear product and operating logic.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10, rotateY: 5, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center text-center border border-gray-100 transform transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-secondary-100 to-secondary-50 rounded-2xl flex items-center justify-center mb-6 transform transition-transform duration-300 group-hover:scale-110">
                  <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 3, repeat: Infinity }}>
                    <Trophy size={28} className="text-secondary-600" />
                  </motion.div>
                </div>
                <h3 className="text-xl font-bold mb-4">Operator-Minded Review</h3>
                <p className="text-gray-600">
                  We look at product quality, customer value, market positioning, and how the business actually runs online.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -10, rotateY: 5, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                className="bg-white p-8 rounded-2xl shadow-xl flex flex-col items-center text-center border border-gray-100 transform transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-accent-100 to-accent-50 rounded-2xl flex items-center justify-center mb-6 transform transition-transform duration-300 group-hover:scale-110">
                  <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                    <LineChart size={28} className="text-accent-600" />
                  </motion.div>
                </div>
                <h3 className="text-xl font-bold mb-4">Long-Term Build Quality</h3>
                <p className="text-gray-600">
                  Our interest is in improving durable digital businesses over time through stronger product and execution.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
